import { c as _c } from "react/compiler-runtime";
import type { OptionalNumber } from '@/lib/piano/touch-design/measured-key.requirements';
import Joi from 'joi';
import { TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { useValidatedNumericInputField } from '@/hooks/use-validated-numeric-input-field';
import { cn } from '@/lib/utils';
export interface MeasureInputFieldProps {
  'aria-label'?: string;
  className?: string;
  defaultValue: OptionalNumber;
  /** The quantity the field holds: the table finds the next field by it. */
  name?: string;
  onUpdate: (value: OptionalNumber) => void;
  placeholder?: string;
  validator?: Joi.Schema;
  /**
   * A reading that is a number but cannot be right, such as an up weight
   * above the down weight. The value is kept; the field is ringed in red and
   * says why: at once to a screen reader, and in a tooltip on focus — which
   * a tap gives, where a hover does not exist.
   */
  warning?: string;
}

/**
 * One measured value.
 *
 * A field that has been answered is marked by a full-strength rule rather than
 * a fill: the table is read as a whole, and an answered field should settle
 * back rather than stand out.
 */
export const MeasureInputField: React.FC<MeasureInputFieldProps> = t0 => {
  const $ = _c(35);
  const {
    "aria-label": ariaLabel,
    className,
    defaultValue,
    name,
    onUpdate,
    placeholder,
    validator,
    warning
  } = t0;
  const warningId = useId();
  const {
    error,
    inputValue,
    onInputChange
  } = useValidatedNumericInputField(defaultValue, onUpdate, validator);
  const [isFocused, setIsFocused] = useState(false);
  const shouldShowErrorState = !!error && isFocused;
  let t1;
  if ($[0] !== error || $[1] !== inputValue) {
    t1 = !error && !["", "-"].includes(inputValue);
    $[0] = error;
    $[1] = inputValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shouldShowFilledState = t1;
  const shouldShowWarningState = !error && !!warning;
  const t2 = shouldShowFilledState && "border-ptl-rule-strong";
  const t3 = shouldShowErrorState && "border-destructive bg-destructive/10 text-destructive";
  const t4 = shouldShowWarningState && "border-destructive text-destructive";
  let t5;
  if ($[3] !== className || $[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = cn("h-8 w-16 rounded-md border border-input bg-card px-2 text-center font-mono text-sm text-foreground outline-none transition-colors max-md:text-base", "placeholder:text-ptl-ink-3", "focus-visible:border-ptl-current focus-visible:ring-2 focus-visible:ring-ptl-current/40", t2, t3, t4, className);
    $[3] = className;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const inputClassName = t5;
  const t6 = isFocused && (!!error || shouldShowWarningState);
  const t7 = shouldShowWarningState ? warningId : undefined;
  const t8 = inputValue ?? undefined;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsFocused(true);
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== onInputChange) {
    t10 = e => {
      setIsFocused(false);
      onInputChange(e);
    };
    $[9] = onInputChange;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== ariaLabel || $[12] !== inputClassName || $[13] !== name || $[14] !== onInputChange || $[15] !== placeholder || $[16] !== t10 || $[17] !== t7 || $[18] !== t8) {
    t11 = <TooltipTrigger asChild={true}><input type="text" inputMode="decimal" enterKeyHint="next" aria-label={ariaLabel} aria-describedby={t7} name={name} placeholder={placeholder} value={t8} onChange={onInputChange} onFocus={t9} onBlur={t10} className={inputClassName} /></TooltipTrigger>;
    $[11] = ariaLabel;
    $[12] = inputClassName;
    $[13] = name;
    $[14] = onInputChange;
    $[15] = placeholder;
    $[16] = t10;
    $[17] = t7;
    $[18] = t8;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== error || $[21] !== shouldShowWarningState || $[22] !== warning) {
    t12 = error || shouldShowWarningState ? <TooltipContent variant="destructive">{error ?? warning}</TooltipContent> : null;
    $[20] = error;
    $[21] = shouldShowWarningState;
    $[22] = warning;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12 || $[26] !== t6) {
    t13 = <Tooltip open={t6}>{t11}{t12}</Tooltip>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t6;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== shouldShowWarningState || $[29] !== warning || $[30] !== warningId) {
    t14 = shouldShowWarningState ? <><TriangleAlert aria-hidden="true" className="size-3.5 shrink-0 text-destructive" /><span id={warningId} className="sr-only">{warning}</span></> : null;
    $[28] = shouldShowWarningState;
    $[29] = warning;
    $[30] = warningId;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <span className="inline-flex items-center gap-1.5">{t13}{t14}</span>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
};